import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Role, TenantCustomRole } from "@/types/user";
import {
  useCustomRoles,
  useReplaceUserCustomRoles,
  useUserCustomRoleAssignments,
} from "@/components/pages/users/hooks/useCustomRoles";
import { formatCustomRoleError } from "@/components/pages/users/utils/customRoleErrors";

type UserCustomRolesPanelProps = {
  userId: string;
  userRole: Role | "SUPER_ADMIN" | "GUESTERIA_ADMIN";
};

const groupPermissionsByModule = (
  permissions: string[],
): Record<string, string[]> => {
  const result: Record<string, string[]> = {};
  for (const key of permissions) {
    const [module = "other"] = key.split(":");
    if (!result[module]) {
      result[module] = [];
    }
    result[module].push(key);
  }
  for (const module of Object.keys(result)) {
    result[module].sort();
  }
  return result;
};

const formatTimestamp = (iso: string | null | undefined): string => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

export const UserCustomRolesPanel: React.FC<UserCustomRolesPanelProps> = ({
  userId,
  userRole,
}) => {
  const { t } = useTranslation();
  const { data: allRoles = [] } = useCustomRoles();
  const { data: assignments = [] } = useUserCustomRoleAssignments(
    userId,
    Boolean(userId),
  );
  const replaceRolesMutation = useReplaceUserCustomRoles(userId);
  const [lastEffectivePermissions, setLastEffectivePermissions] =
    React.useState<string[] | null>(null);
  const [isEffectiveOpen, setIsEffectiveOpen] = React.useState(false);

  const isAssignmentBlocked =
    userRole === "SUPER_ADMIN" || userRole === "GUESTERIA_ADMIN";

  const rolesForUser = React.useMemo(
    () =>
      isAssignmentBlocked
        ? []
        : allRoles.filter(
            (role) =>
              role.baseRole === (userRole as TenantCustomRole["baseRole"]),
          ),
    [allRoles, isAssignmentBlocked, userRole],
  );

  const assignedRoleId = assignments[0]?.customRoleId ?? null;
  const [draftRoleId, setDraftRoleId] = React.useState<string | null>(
    assignedRoleId,
  );

  React.useEffect(() => {
    setDraftRoleId(assignedRoleId);
  }, [assignedRoleId, userId]);

  const groupedEffective = React.useMemo(
    () =>
      lastEffectivePermissions
        ? groupPermissionsByModule(lastEffectivePermissions)
        : null,
    [lastEffectivePermissions],
  );

  const isDirty = draftRoleId !== assignedRoleId || assignments.length > 1;

  if (isAssignmentBlocked) return null;

  const toggleRole = (roleId: string) => {
    setDraftRoleId((previousRoleId) =>
      previousRoleId === roleId ? null : roleId,
    );
  };

  const onSave = async () => {
    try {
      const result = await replaceRolesMutation.mutateAsync(
        draftRoleId ? [draftRoleId] : [],
      );
      setLastEffectivePermissions(result.effectivePermissions);
      setIsEffectiveOpen(true);
      toast.success(
        t(
          "users.roles.assignmentSaved",
          "Custom roles updated. Permission changes take effect on next sign-in.",
        ),
      );
    } catch (error) {
      const knownRoleNamesById = new Map(
        allRoles.map((role) => [role.id, role.name]),
      );
      toast.error(
        formatCustomRoleError(error, knownRoleNamesById) ||
          t(
            "users.roles.assignmentFailed",
            "Failed to update custom role assignments for this user",
          ),
      );
      console.error(error);
    }
  };

  return (
    <div className="mx-auto mt-6 w-full max-w-5xl space-y-4 rounded-xl border border-[#E5E7EB] p-4">
      <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <p>
          {t(
            "users.roles.propagationBanner",
            "Permission changes take effect on the user's next sign-in.",
          )}
        </p>
      </div>

      <div>
        <h3 className="text-lg font-medium">
          {t("users.roles.customAssignments", "Custom Roles")}
        </h3>
        <p className="mb-3 text-sm text-[#6B7280]">
          {t(
            "users.roles.customAssignmentsHint",
            "Select one custom role matching the user's system role.",
          )}
        </p>

        {assignments.length > 0 ? (
          <div className="mb-3 flex flex-wrap gap-2">
            {assignments.map((assignment) => (
              <Badge
                key={assignment.customRoleId}
                variant="secondary"
                className="flex flex-col items-start gap-0.5 px-2 py-1 text-xs"
              >
                <span className="font-medium">{assignment.customRoleName}</span>
                <span className="text-[10px] text-[#6B7280]">
                  {t("users.roles.assignedAt", {
                    defaultValue: "Assigned {{at}}",
                    at: formatTimestamp(assignment.assignedAt),
                  })}
                </span>
              </Badge>
            ))}
          </div>
        ) : null}

        <div className="space-y-2">
          {rolesForUser.map((role) => (
            <label key={role.id} className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={draftRoleId === role.id}
                onCheckedChange={() => toggleRole(role.id)}
              />
              <span className="font-medium">{role.name}</span>
              <span className="text-[#6B7280]">
                ({role.permissionKeys.length} permissions)
              </span>
            </label>
          ))}
          {rolesForUser.length === 0 ? (
            <p className="text-sm text-[#6B7280]">
              {t(
                "users.roles.noCustomRolesForUserRole",
                "No custom roles available for this user role.",
              )}
            </p>
          ) : null}
        </div>

        <div className="mt-4 flex justify-end">
          <Button
            onClick={() => void onSave()}
            disabled={replaceRolesMutation.isPending || !isDirty}
          >
            {replaceRolesMutation.isPending
              ? t("users.form.updating")
              : t("users.roles.saveAssignments", "Save assignments")}
          </Button>
        </div>
      </div>

      {groupedEffective ? (
        <div className="rounded-md border border-[#E5E7EB] bg-[#F9FAFB] p-3">
          <button
            type="button"
            onClick={() => setIsEffectiveOpen((prev) => !prev)}
            className="flex w-full items-center justify-between gap-2 text-sm font-medium text-[#0A0A0A]"
          >
            <span>
              {t("users.roles.effectivePermissions", {
                defaultValue: "Effective permissions ({{count}})",
                count: lastEffectivePermissions?.length ?? 0,
              })}
            </span>
            {isEffectiveOpen ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
          {isEffectiveOpen ? (
            <div className="mt-3 space-y-3">
              {Object.entries(groupedEffective).map(([module, keys]) => (
                <div key={module}>
                  <p className="mb-1 text-xs font-medium tracking-wide text-[#6B7280] uppercase">
                    {module}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {keys.map((key) => (
                      <Badge
                        key={key}
                        variant="outline"
                        className="text-[11px]"
                      >
                        {key}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
